import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import * as CapLedgerService from '@/services/capLedgerService';
import {
  planShareBalanceChange,
  type ShareBalancePlan,
  type ReductionReason,
} from '@/utils/shareBalanceChange';
import { ReductionReasonField } from '@/components/ledger/ReductionReasonField';

interface ShareholderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  companies: Array<{ id: string; name: string }>;
  preselectedCompanyId?: string;
  /**
   * Whether this dialog may move shares. Set false where the surrounding flow
   * is itself recording the issuance — LedgerEventDialog quick-adds a holder
   * and then writes their event, and offering a share count in both places
   * issues twice.
   */
  allowShareEntry?: boolean;
  editingShareholder?: {
    id: string;
    name: string;
    email?: string;
    address?: string;
    tax_id?: string;
    entity_type: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
    company_id: string;
    /**
     * Present holding, as the register reports it. Shares are not stored on the
     * shareholder — this is the signed sum of their ledger events, and editing
     * the field below writes another event rather than updating a column.
     */
    total_shares?: number;
  };
}

export const ShareholderDialog = ({
  open,
  onOpenChange,
  onSuccess,
  companies,
  preselectedCompanyId,
  editingShareholder,
  allowShareEntry = true,
}: ShareholderDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(
    editingShareholder?.company_id || preselectedCompanyId || ''
  );
  const [name, setName] = useState(editingShareholder?.name || '');
  const [email, setEmail] = useState(editingShareholder?.email || '');
  const [address, setAddress] = useState(editingShareholder?.address || '');
  const [taxId, setTaxId] = useState(editingShareholder?.tax_id || '');
  const [entityType, setEntityType] = useState<'INDIVIDUAL' | 'COMPANY' | 'TRUST'>(
    editingShareholder?.entity_type || 'INDIVIDUAL'
  );
  const [shares, setShares] = useState(
    editingShareholder ? String(editingShareholder.total_shares ?? 0) : ''
  );

  const [reductionReason, setReductionReason] = useState<ReductionReason | undefined>();

  const currentShares = editingShareholder?.total_shares ?? 0;

  // Planned without a reason on purpose: a 'needs-reason' answer is how the form
  // learns the holding is going down and that the question has to be asked.
  const reductionPreview = useMemo(
    () => (allowShareEntry ? planShareBalanceChange(currentShares, shares) : undefined),
    [allowShareEntry, currentShares, shares]
  );
  const isReducing = reductionPreview?.kind === 'needs-reason';

  // Update state when editingShareholder changes
  useEffect(() => {
    if (editingShareholder) {
      setSelectedCompany(editingShareholder.company_id);
      setName(editingShareholder.name);
      setEmail(editingShareholder.email || '');
      setAddress(editingShareholder.address || '');
      setTaxId(editingShareholder.tax_id || '');
      setEntityType(editingShareholder.entity_type);
      setShares(String(editingShareholder.total_shares ?? 0));
      setReductionReason(undefined);
    } else {
      resetForm();
    }
  }, [editingShareholder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompany || !name) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in company and name',
        variant: 'destructive',
      });
      return;
    }

    // Decided before anything is written, so a bad share count cannot leave the
    // details saved and the balance half-applied.
    const sharePlan: ShareBalancePlan = allowShareEntry
      ? planShareBalanceChange(currentShares, shares, reductionReason)
      : { kind: 'unchanged' };
    if (sharePlan.kind === 'invalid') {
      toast({
        title: 'Invalid share count',
        description: sharePlan.error,
        variant: 'destructive',
      });
      return;
    }
    if (sharePlan.kind === 'needs-reason') {
      toast({
        title: 'Say why the holding is going down',
        description: 'Choose whether the shares are cancelled or the balance is being corrected.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    let shareholderId = editingShareholder?.id;

    try {
      if (editingShareholder) {
        // Update existing shareholder
        await CapLedgerService.updateShareholder(editingShareholder.id, {
          name,
          email: email || undefined,
          address: address || undefined,
          tax_id: taxId || undefined,
          entity_type: entityType,
        });
      } else {
        // Create new shareholder
        const created = await CapLedgerService.createShareholder({
          company_id: selectedCompany,
          name,
          email: email || undefined,
          address: address || undefined,
          tax_id: taxId || undefined,
          entity_type: entityType,
        });
        shareholderId = created.id;
      }
    } catch (error: any) {
      toast({
        title: editingShareholder ? 'Error updating shareholder' : 'Error creating shareholder',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    // The share count is a second, separate write: the register records the
    // movement rather than the balance. Reported on its own so a failure here is
    // not mistaken for the details failing to save.
    if (sharePlan.kind === 'change') {
      try {
        await CapLedgerService.insertLedgerEvent({
          companyId: selectedCompany,
          shareholderId: shareholderId!,
          eventType: sharePlan.eventType,
          shares: sharePlan.shares,
          shareClass: 'Ordinary',
          noteReference: sharePlan.noteReference,
        });
        await CapLedgerService.recalcCapTable(selectedCompany);
      } catch (error: any) {
        toast({
          title: 'Details saved, but the share count was not changed',
          description: error.message,
          variant: 'destructive',
        });
        // Refresh, because the details did save — but deliberately no
        // onOpenChange(false) and no resetForm(). The form stays open holding
        // the count that was rejected, so it can be corrected and resubmitted
        // rather than retyped from memory against a message that has faded.
        onSuccess();
        setLoading(false);
        return;
      }
    }

    toast({
      title: 'Success',
      description: editingShareholder
        ? 'Shareholder updated successfully'
        : 'Shareholder created successfully',
    });

    onSuccess();
    onOpenChange(false);
    resetForm();
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedCompany(preselectedCompanyId || '');
    setName('');
    setEmail('');
    setAddress('');
    setTaxId('');
    setEntityType('INDIVIDUAL');
    setShares('');
    setReductionReason(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The base DialogContent sets no height, so the form simply grew past the
          viewport when the reduction question appeared and took the Cancel and
          Update buttons off-screen with it — unreachable, on a laptop. */}
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingShareholder ? 'Edit Shareholder' : 'Add Shareholder'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company">Company *</Label>
            <Select
              value={selectedCompany}
              onValueChange={setSelectedCompany}
              disabled={!!editingShareholder}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          {allowShareEntry && (
            <div className="space-y-2">
              <Label htmlFor="shares">Shares</Label>
              <Input
                id="shares"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {editingShareholder
                  ? `Currently ${currentShares.toLocaleString()}. Changing this records a movement in the share register.`
                  : 'Leave blank to add the shareholder without issuing shares yet.'}
              </p>

              {reductionPreview?.kind === 'needs-reason' && (
                <ReductionReasonField
                  reducingBy={reductionPreview.reducingBy}
                  value={reductionReason}
                  onChange={setReductionReason}
                />
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="taxId">Tax ID</Label>
            <Input
              id="taxId"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="123-45-6789"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="entityType">Entity Type *</Label>
            <Select
              value={entityType}
              onValueChange={(value: 'INDIVIDUAL' | 'COMPANY' | 'TRUST') => setEntityType(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select entity type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                <SelectItem value="COMPANY">Company (Legal Entity)</SelectItem>
                <SelectItem value="TRUST">Trust</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            {/* Blocked rather than warned about: the answer decides what the
                register says happened, so there is nothing sensible to submit
                until it is given. */}
            <Button
              type="submit"
              disabled={loading || (isReducing && !reductionReason)}
              className="flex-1"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingShareholder ? 'Update Shareholder' : 'Add Shareholder'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
